'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/design-system/shadcn-ui/sheet';
import {
  HeaderNav,
  HEADER_NAV_ITEMS,
  MOBILE_MENU_LINK_ITEMS,
  MOBILE_MENU_SECTIONS,
} from './club-header-nav';
import {
  HamburgerMenu,
  PcMyPageMenuPanel,
  MyPageMenu,
} from './club-header-menus';

export type { NavItem } from './club-header-nav';

/** メニュー開時用オーバーレイのスタイル */
const overlayStyle: React.CSSProperties = {
  background: 'rgba(0, 0, 0, 0.40)',
  backdropFilter: 'blur(9.5px)',
  WebkitBackdropFilter: 'blur(9.5px)',
};

const HOVER_CLOSE_DELAY_MS = 150;

export function ClubHeader() {
  const [isSpMenuOpen, setSpMenuOpen] = useState(false);
  const [isPcMyPageMenuOpen, setPcMyPageMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const isLoggedIn = useAuthStore((s) => s.isAuthenticated);
  const [headerHeight, setHeaderHeight] = useState(0);
  const pcMenuCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    return () => {
      if (pcMenuCloseTimeoutRef.current) {
        clearTimeout(pcMenuCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const updateHeight = () => {
      const h = el.offsetHeight;
      setHeaderHeight(h);
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    };
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // メニュー開時はスクロールロック
  useEffect(() => {
    if (isSpMenuOpen || isPcMyPageMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isPcMyPageMenuOpen, isSpMenuOpen]);

  const closeSpMenu = () => setSpMenuOpen(false);
  const closePcMyPageMenu = useCallback(() => {
    setPcMyPageMenuOpen(false);
  }, []);

  const cancelPcMenuClose = useCallback(() => {
    if (pcMenuCloseTimeoutRef.current) {
      clearTimeout(pcMenuCloseTimeoutRef.current);
      pcMenuCloseTimeoutRef.current = null;
    }
  }, []);

  const schedulePcMenuClose = useCallback(() => {
    cancelPcMenuClose();
    pcMenuCloseTimeoutRef.current = setTimeout(() => {
      pcMenuCloseTimeoutRef.current = null;
      setPcMyPageMenuOpen(false);
    }, HOVER_CLOSE_DELAY_MS);
  }, [cancelPcMenuClose]);

  const handlePcMyPageMenuEnter = useCallback(() => {
    cancelPcMenuClose();
    setPcMyPageMenuOpen(true);
  }, [cancelPcMenuClose]);

  const handlePcMyPageMenuLeave = useCallback(() => {
    schedulePcMenuClose();
  }, [schedulePcMenuClose]);

  return (
    <>
      <header
        ref={headerRef}
        className={cn(
          'sticky top-0 bg-white',
          isSpMenuOpen || isPcMyPageMenuOpen
            ? 'z-[55] border-b border-club-gray-200'
            : 'z-50',
        )}
      >
        <div className="w-full mx-auto max-w-xl md:max-w-[1280px] px-6 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between py-club-8 px-0 md:h-[94px] md:pt-club-32 md:pb-[28px] md:items-stretch">
            <HeaderNav
              navItems={HEADER_NAV_ITEMS}
              isSpMenuOpen={isSpMenuOpen}
              isPcMyPageMenuOpen={isPcMyPageMenuOpen}
              onToggleSpMenu={() => {
                closePcMyPageMenu();
                setSpMenuOpen((prev) => !prev);
              }}
              onPcMyPageMenuEnter={handlePcMyPageMenuEnter}
              onPcMyPageMenuLeave={handlePcMyPageMenuLeave}
              onSpMyPageMenuClick={() => {
                setSpMenuOpen(false);
                setPcMyPageMenuOpen(true);
              }}
              onCloseSpMyPageMenu={closePcMyPageMenu}
            />
            {/* SP用スペーサー */}
            <div className="w-full md:hidden" />
            <MyPageMenu />
          </div>
        </div>
      </header>

      <Sheet
        open={isSpMenuOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeSpMenu();
          }
        }}
      >
        <SheetContent
          side="top"
          overlayTop={headerHeight}
          className="left-0 right-0 top-0 z-[50] border-b-0 bg-transparent p-0 shadow-none md:hidden"
          style={{ top: headerHeight }}
        >
          <SheetTitle className="sr-only">
            Club MEDULLA メインメニュー
          </SheetTitle>
          <div className="max-h-[calc(100vh-var(--header-height,94px))] overflow-auto bg-white">
            <HamburgerMenu
              open
              sections={MOBILE_MENU_SECTIONS}
              linkItems={MOBILE_MENU_LINK_ITEMS}
              onNavigate={() => {
                closePcMyPageMenu();
                closeSpMenu();
              }}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* PC: マイページメニュー開時 */}
      {isPcMyPageMenuOpen && (
        <>
          <div
            className="fixed left-0 right-0 bottom-0 z-[50] hidden md:block"
            style={{ top: headerHeight }}
          >
            <div
              role="presentation"
              aria-hidden
              className="absolute inset-0"
              style={overlayStyle}
              onClick={closePcMyPageMenu}
            />
            <PcMyPageMenuPanel
              isLoggedIn={isLoggedIn}
              onNavigate={closePcMyPageMenu}
              onMouseEnter={handlePcMyPageMenuEnter}
              onMouseLeave={handlePcMyPageMenuLeave}
            />
          </div>
          {/* SP: マイページメニュー開時 */}
          <div
            className="fixed left-0 right-0 bottom-0 z-[50] flex flex-col md:hidden"
            style={{ top: headerHeight }}
          >
            <div className="max-h-[calc(100vh-var(--header-height,94px))] shrink-0 overflow-auto bg-white">
              <PcMyPageMenuPanel
                isLoggedIn={isLoggedIn}
                isSp
                onNavigate={closePcMyPageMenu}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
              />
            </div>
            <div
              role="presentation"
              aria-hidden
              className="min-h-0 flex-1"
              style={overlayStyle}
              onClick={closePcMyPageMenu}
            />
          </div>
        </>
      )}
    </>
  );
}

export default ClubHeader;
